import { useState } from "react";
import { useGlobals } from "@/components/providers/globals-provider.tsx";
import useSmartnumberWishlistStatus from "@/features/sub-families/hooks/use-smartnumber-wishlist-status.ts";
import DiagnosticsStore from "@/features/diagnostics/store/diagnostics-store.ts";
import { getValidApiToken } from "@/lib/api-token.ts";
import { loosePostRequestBuilder } from "@/lib/fetcher.ts";
import Logger from "@/lib/logger.ts";
import { itemListSetWishlistStatusAction } from "@/redux/slices/item-list-slice.ts";
import { useAppDispatch, useAppSelector } from "@/redux/store.ts";
import { Site } from "@/types";

export default function useSmartnumberFavorites() {
    const { site, locale, apiTokenUrl } = useGlobals();

    const dispatch = useAppDispatch();
    const smartNumber = useAppSelector((state) => state.smartNumber.calculatedResult);
    const paths = useAppSelector((state) => state.itemList);
    const onWishlist = useAppSelector((state) => state.itemList.isOnWishlist);

    const [isPending, setIsPending] = useState<boolean>(false);

    useSmartnumberWishlistStatus();

    const isNorthAmerica = site === Site.NorthAmerica;
    const canUseWishlist =
        !isNorthAmerica && !!paths.wishlistAddPath && !!paths.wishlistRemovePath && !!apiTokenUrl;

    const toggleWishlist = async () => {
        if (!smartNumber || !canUseWishlist || !apiTokenUrl) return;

        const path = onWishlist ? paths.wishlistRemovePath : paths.wishlistAddPath;
        if (!path) return;

        const token = await getValidApiToken(apiTokenUrl);
        if (!token) {
            Logger.error("Cannot toggle wishlist without a valid API token");
            return;
        }

        const url = new URL(path);
        const post = loosePostRequestBuilder(site, locale);

        setIsPending(true);

        try {
            const response = await post<{ success: boolean; message?: string }, { success: false }>(
                url,
                {
                    smartnumber: smartNumber,
                },
                { authorization: token },
            );

            if (response.success) {
                dispatch(itemListSetWishlistStatusAction(!onWishlist));
            }
        } catch (err) {
            Logger.error("Failed toggling smartnumber on wishlist", err);
            DiagnosticsStore.collectEventLog("error", "Failed toggling smartnumber on wishlist");
        } finally {
            setIsPending(false);
        }
    };

    return {
        onWishlist,
        isPending,
        canUseWishlist,
        toggleWishlist,
    };
}
